import { apiClient } from '../client';
import { dataServiceClient } from '../dataservice-client';
import { skillNameOf, skillInputOf } from '../../utils/display-text';
import type {
  LogsOutput,
  HistoryResponse,
  History,
  PreviewInput,
  RuntimeInput,
  AgentExecutionContext,
  AgentExecutionLog,
  GetExecutionHistoryParams,
  ExecutionTokenUsage,
} from '../types/executions';
import type { UIFormElements } from '../types/process-flows';
import { knowledgeBaseApi } from './knowledgeBase';

/**
 * Execution service.
 *
 * Talks directly to the shared data-service `execute_flow` API via `/data-api`,
 * exactly like Wexa-web (see Wexa-web src/api/executions). `executed_by` is
 * derived by the data-service from the Bearer identity, and `projectID` is a
 * query param — so this uses the user-context dataServiceClient (no stsk).
 *
 * The data-service execution responses use the *Wexa* shape (maps keyed by
 * agent/preview id). The harness execution UI consumes flattened arrays, so the
 * read endpoints below adapt map -> array. The inner item shapes already match
 * (the harness execution types are ported from Wexa-web).
 */

const pid = (projectID: string) => encodeURIComponent(projectID);

/** UIFormElements[] -> { field_id: value } map (execution input variables). */
function toInputVariables(inputs: UIFormElements[]): Record<string, any> {
  const out: Record<string, any> = {};
  for (const el of inputs) out[el.id] = (el as any).value;
  return out;
}

/** UIFormElements[] -> { field_id: stringValue } map (runtime human input). */
function toValuesMap(inputs: UIFormElements[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const el of inputs) {
    const v = (el as any).value;
    out[el.id] = Array.isArray(v) ? v.join(',') : v == null ? '' : String(v);
  }
  return out;
}

// ── Data-service (Wexa) execution response shapes (only what we read) ────────
interface DsPreviewItem {
  is_approved: boolean;
  preview_input: PreviewInput;
}
interface DsLogsOutput {
  goal: string;
  status: LogsOutput['status'];
  execution_id: string;
  agentflow_id: string;
  agentflow?: LogsOutput['agentflow'];
  conclusion?: LogsOutput['conclusion'];
  created_at?: number;
  end_time?: number | string | null;
  previews?: Record<string, DsPreviewItem>;
  runtime_inputs?: Record<string, RuntimeInput>;
  execution_context?: Record<string, AgentExecutionContext>;
  agents_output?: AgentExecutionLog[];
  analytics?: { overall?: { total_price?: number; total_tokens?: number } };
  executed_by?: {
    name?: string;
    type?: History['type'];
    metadata?: Record<string, any> | null;
    is_external_application?: boolean;
    application_user_id?: string | null;
  } | null;
  trigger_source?: History['trigger_source'];
  agent_type?: History['agent_type'];
  duration_ms?: number | null;
  total_tokens?: number | null;
  total_price?: number | null;
  parent_execution_id?: string | null;
  input_variables?: Record<string, any> | null;
  interop_ask?: LogsOutput['interop_ask'];
  simulation?: boolean | null;
  has_simulated_output?: boolean | null;
}
interface DsHistoryItem {
  execution_id: string;  // data-service uses execution_id (UUID), not _id
  _id?: string;          // keep as fallback
  goal: string;
  status: History['status'];
  projectID: string;
  created_at: number;
  end_time?: number;
  agentflow_id: string;
  agentflow_name?: string;
  executed_by?:
    | {
        name?: string;
        type?: History['type'];
        metadata?: Record<string, any> | null;
        is_external_application?: boolean;
        application_user_id?: string | null;
      }
    | string;
  // FAB-44: conversation/session id — needed so the Executions list can group
  // same-session turns into one row.
  session_id?: string | null;
  trigger_source?: History['trigger_source'];
  agent_type?: History['agent_type'];
  duration_ms?: number | null;
  total_tokens?: number | null;
  total_price?: number | null;
  agent_version?: number | null;
  parent_execution_id?: string | null;
  simulation?: boolean | null;
  has_simulated_output?: boolean | null;
}
interface DsHistoryResponse {
  data: DsHistoryItem[];
  total_count: number;
  status_counts?: Record<string, number>;
}

/** DS prompt-variable types -> UIFormElements types the renderer understands. */
function dsFieldType(t?: string): UIFormElements['type'] {
  switch ((t ?? '').toLowerCase()) {
    case 'number': case 'integer': case 'int': case 'float': return 'numeric_text';
    case 'boolean': case 'bool': return 'checkbox';
    case 'long_text': case 'textarea': case 'text_area': return 'long_text';
    default: return 'text';
  }
}

/** Resolve an agent's display title from the execution doc. */
function agentTitleOf(ds: DsLogsOutput, agentId: string): string {
  const fromCtx = (ds.execution_context as any)?.[agentId]?.agent_title;
  if (fromCtx) return fromCtx;
  const agents = (ds.agentflow?.agents ?? []) as any[];
  return agents.find((a) => String(a?._id ?? a?.agent_id) === agentId)?.title ?? '';
}

/**
 * The data-service keys runtime inputs by agent id and uses the *Wexa* entry
 * shape `{fields: [{field_id, label, description, type}], values, is_submitted}`
 * — NOT the harness `{agent_id, agent_title, form_elements}` the UI renders.
 * Feeding the raw entry into RuntimeInputPanel crashes on
 * `form_elements.forEach` (the input_required "Something went wrong" screen).
 */
function adaptRuntimeInputs(ds: DsLogsOutput): RuntimeInput[] {
  return Object.entries((ds.runtime_inputs ?? {}) as Record<string, any>)
    .filter(([, v]) => v && !v.is_submitted)
    .map(([agentId, v]) => {
      if (Array.isArray(v.form_elements)) {
        // Already harness-shaped — pass through, backfilling identity fields.
        return {
          agent_id: v.agent_id ?? agentId,
          agent_title: v.agent_title ?? agentTitleOf(ds, agentId),
          form_elements: v.form_elements,
        };
      }
      const fields: any[] = Array.isArray(v.fields) ? v.fields.filter(Boolean) : [];
      const form_elements = fields.map((f) => ({
        id: f.field_id ?? f.id ?? '',
        type: dsFieldType(f.type),
        title: f.label || f.field_id || 'Input',
        description: f.description ?? '',
        required: true,
        is_advanced_setting: false,
        value: v.values?.[f.field_id] ?? '',
      })) as UIFormElements[];
      return { agent_id: agentId, agent_title: agentTitleOf(ds, agentId), form_elements };
    });
}

/**
 * Preview entries carry the raw LLM validated_output `{thoughts, skill:{name,
 * id, inputs}}` rather than the harness PreviewInput shape. Derive the display
 * fields but KEEP the original keys — approve/reject PUTs the object back and
 * the backend resumes the skill from it.
 */
function adaptPreviewInputs(ds: DsLogsOutput): PreviewInput[] {
  return Object.entries((ds.previews ?? {}) as Record<string, any>)
    .filter(([, p]) => p && !p.is_approved)
    .map(([agentId, p]) => {
      const pi = (p.preview_input ?? {}) as any;
      return {
        ...pi,
        agent_id: pi.agent_id ?? agentId,
        agent_title: pi.agent_title ?? agentTitleOf(ds, agentId),
        skill_name: pi.skill_name ?? skillNameOf(pi.skill),
        skill_input: pi.skill_input ?? skillInputOf(pi.skill) ?? {},
        ...((p.approval_id ?? pi.approval_id) ? { approval_id: p.approval_id ?? pi.approval_id } : {}),
      };
    });
}

/**
 * True when `value` (or anything nested in it) carries `_simulated: true`, the
 * marker every mocked skill call and simulated user connector puts on its
 * placeholder output. Bounded so a huge payload cannot stall a render.
 */
export function containsSimulatedMarker(value: unknown, maxDepth = 6): boolean {
  let budget = 5000;
  const walk = (node: unknown, depth: number): boolean => {
    if (budget-- <= 0 || depth > maxDepth || node == null || typeof node !== 'object') return false;
    if (Array.isArray(node)) return node.some((v) => walk(v, depth + 1));
    const rec = node as Record<string, unknown>;
    if (rec._simulated === true) return true;
    return Object.values(rec).some((v) => walk(v, depth + 1));
  };
  return walk(value, 0);
}

/** Client-side fallback for has_simulated_output when the backend omits it. */
function scanSimulatedOutput(ds: DsLogsOutput): boolean {
  const ctx = ds.execution_context;
  if (ctx && typeof ctx === 'object' && Object.values(ctx).some((c) => containsSimulatedMarker(c?.output_data))) {
    return true;
  }
  return Array.isArray(ds.agents_output) && ds.agents_output.some((l) => containsSimulatedMarker(l?.output_data));
}

/** Adapt the data-service execution doc to the harness LogsOutput (maps -> arrays). */
export function adaptLogs(ds: DsLogsOutput): LogsOutput {
  // execution_context is stored LZ4-compressed; the backend normally
  // decompresses it, but if a raw string ever slips through, Object.values
  // over a string yields one bogus "context" per character.
  const ctx = ds.execution_context;
  const endTime = typeof ds.end_time === 'number' ? ds.end_time : undefined;
  const durationMs =
    ds.duration_ms ??
    (endTime && ds.created_at && endTime >= ds.created_at
      ? Math.round((endTime - ds.created_at) * 1000)
      : undefined);
  return {
    _id: ds.execution_id,
    status: ds.status,
    goal: ds.goal,
    agentflow: ds.agentflow as LogsOutput['agentflow'],
    // Preserve the map key so presentation layers can distinguish the
    // synthetic "conclusion" step from a real agent named "Conclusion".
    agent_execution_contexts:
      ctx && typeof ctx === 'object'
        ? Object.entries(ctx).map(([agent_id, detail]) => ({ ...detail, agent_id }))
        : [],
    // Do not derive activity from execution_context: that map is intentionally
    // overwritten as each agent advances. agents_output is the backend's
    // append-only event stream and is what keeps completed steps visible while
    // a later poll reports the next step.
    agents_output: Array.isArray(ds.agents_output) ? ds.agents_output : [],
    // Only previews still awaiting approval surface to the approval panel.
    preview_inputs: adaptPreviewInputs(ds),
    runtime_inputs: adaptRuntimeInputs(ds),
    conclusion: ds.conclusion,
    total_price: ds.total_price ?? ds.analytics?.overall?.total_price,
    created_at: ds.created_at,
    session_id: (ds as any).session_id ?? null, // FAB-44
    ...(((ds as any).approval_id ?? (ds as any).anomaly_detected?.approval_id)
      ? { approval_id: (ds as any).approval_id ?? (ds as any).anomaly_detected?.approval_id }
      : {}),
    ...((ds as any).anomaly_detected?.restarted_execution_id
      ? { restarted_execution_id: (ds as any).anomaly_detected.restarted_execution_id }
      : {}),
    executed_by: ds.executed_by ?? undefined,
    trigger_source:
      ds.trigger_source ??
      deriveTriggerSource(ds.executed_by?.type, ds.parent_execution_id),
    agent_type: ds.agent_type ?? undefined,
    duration_ms: durationMs,
    total_tokens: ds.total_tokens ?? ds.analytics?.overall?.total_tokens,
    parent_execution_id: ds.parent_execution_id,
    input_variables: ds.input_variables,
    interop_ask: ds.interop_ask,
    simulation: ds.simulation === true,
    has_simulated_output: ds.has_simulated_output ?? scanSimulatedOutput(ds),
  };
}

/**
 * The data-service stores execution timestamps as Unix *seconds* (a float from
 * `datetime.now().timestamp()`), but JS `new Date(n)` expects *milliseconds*.
 * Feeding seconds straight in renders 1970 (the "21/01/1970" bug). Scale values
 * that look like seconds (< 1e12 ≈ year 33658) up to ms; pass ms/ISO through.
 */
export function epochToISO(ts: number | string | null | undefined): string {
  if (ts == null || ts === '') return '';
  const n = typeof ts === 'number' ? ts : Number(ts);
  if (!Number.isFinite(n)) return '';
  const ms = n < 1e12 ? n * 1000 : n;
  const dt = new Date(ms);
  return Number.isNaN(dt.getTime()) ? '' : dt.toISOString();
}

/** Client-side fallback mirroring the data-service derive_trigger_source. */
export function deriveTriggerSource(
  type: History['type'] | undefined,
  parentExecutionId?: string | null,
): NonNullable<History['trigger_source']> {
  if (parentExecutionId) return 'flow';
  switch (type) {
    case 'cron': return 'scheduled';
    case 'triggered': return 'webhook';
    case 'api': return 'api';
    case 'failover': return 'flow';
    default: return 'manual';
  }
}

/** Adapt the data-service execution_flows list to the harness HistoryResponse. */
function adaptHistory(ds: DsHistoryResponse, page: number, limit: number): HistoryResponse {
  const data: History[] = (ds.data ?? []).map((h) => {
    const executedBy = typeof h.executed_by === 'object' ? h.executed_by : undefined;
    const type = executedBy?.type ?? 'manual';
    const durationMs =
      h.duration_ms ??
      (h.end_time && h.created_at && h.end_time >= h.created_at
        ? Math.round((h.end_time - h.created_at) * 1000)
        : undefined);
    return {
      _id: h.execution_id ?? h._id,
      agentflow_id: h.agentflow_id,
      projectID: h.projectID,
      status: h.status,
      type,
      goal: h.goal,
      createdAt: epochToISO(h.created_at),
      updatedAt: epochToISO(h.end_time),
      executed_by: executedBy ? executedBy.name : (h.executed_by as string | undefined),
      agentflow_name: h.agentflow_name,
      session_id: h.session_id ?? null,
      trigger_source: h.trigger_source ?? deriveTriggerSource(type, h.parent_execution_id),
      agent_type: h.agent_type ?? 'Task',
      duration_ms: durationMs ?? undefined,
      total_tokens: h.total_tokens,
      total_price: h.total_price,
      agent_version: h.agent_version ?? null,
      parent_execution_id: h.parent_execution_id,
      is_external_application: executedBy?.is_external_application ?? false,
      application_user_id: executedBy?.application_user_id ?? null,
      executed_by_metadata: executedBy?.metadata ?? null,
      simulation: h.simulation === true,
      has_simulated_output: h.has_simulated_output ?? undefined,
    };
  });
  return {
    data,
    totalCount: ds.total_count ?? 0,
    page,
    limit,
    statusCounts: ds.status_counts ?? {},
  };
}

export const executionsService = {
  async createExecution(
    projectID: string,
    flowId: string,
    goal: string,
    inputs: UIFormElements[],
    files?: string[],
    // FAB-44: conversation/session id — passed so every turn of one chat shares it,
    // enabling multi-turn memory + grouped conversation view. Omit for one-shot runs.
    sessionId?: string,
  ): Promise<{ execution_id: string }> {
    const res = await dataServiceClient.post<{ _id: string; execution_id: string }>(
      `/execute_flow?projectID=${pid(projectID)}`,
      {
        agentflow_id: flowId,
        goal,
        input_variables: toInputVariables(inputs),
        ...(files?.length ? { files } : {}),
        ...(sessionId ? { session_id: sessionId } : {}),
      },
    );
    // IMPORTANT: poll/cancel/pause all key off `execution_id` (a UUID), NOT the
    // Mongo `_id` (ObjectId). The data-service stores them as different values
    // and looks records up by `execution_id`, so returning `_id` here 404s every
    // GET /execute_flow/{id}. Fall back to `_id` only if execution_id is absent.
    return { execution_id: res.execution_id ?? res._id };
  },

  async getExecutionHistory(params: GetExecutionHistoryParams): Promise<HistoryResponse> {
    const page = params.page ?? 1;
    const limit = params.limit ?? 10;
    const query = new URLSearchParams();
    query.append('projectID', params.projectID);
    query.append('skip', String(page <= 0 ? 1 : page));
    query.append('limit', String(limit));
    if (params.status) query.append('status', params.status);
    if (params.status_in?.length) query.append('status_in', params.status_in.join(','));
    if (params.type) query.append('execution_type', params.type);
    if (params.trigger_source) query.append('trigger_source', params.trigger_source);
    if (params.agent_type) query.append('agent_type', params.agent_type);
    if (params.search) query.append('search_key', params.search);
    if (params.agentflow_id) query.append('coworker_id', params.agentflow_id);
    if (params.from_date != null) query.append('from_date', String(params.from_date));
    if (params.to_date != null) query.append('to_date', String(params.to_date));
    if (params.sort_by) query.append('sort_by', params.sort_by);
    if (params.sort_order) query.append('sort_order', params.sort_order);

    const res = await dataServiceClient.get<DsHistoryResponse>(
      `/${pid(params.projectID)}/execution_flows?${query.toString()}`,
    );
    return adaptHistory(res, page, limit);
  },

  async getExecutionLogs(projectID: string, executionId: string): Promise<LogsOutput> {
    const res = await dataServiceClient.get<DsLogsOutput>(
      `/execute_flow/${executionId}?projectID=${pid(projectID)}`,
    );
    return adaptLogs(res);
  },

  /**
   * Real per-call token usage for one run, with the model that served each
   * call and why it was chosen (model routing v2). Not project-scoped on the
   * data-service side, so no projectID query param.
   */
  async getExecutionTokenUsage(executionId: string): Promise<ExecutionTokenUsage> {
    const res = await dataServiceClient.get<ExecutionTokenUsage>(
      `/execute_flow/${encodeURIComponent(executionId)}/cost`,
    );
    return { ...res, operations: res?.operations ?? [] };
  },

  // FAB-44: all turns of one conversation (session), ordered oldest-first, so the
  // Executions detail can render the whole chat instead of a single turn.
  async getSessionExecutions(projectID: string, sessionId: string): Promise<import('@/lib/api/types/executions').SessionTurn[]> {
    const res = await dataServiceClient.get<{ turns: import('@/lib/api/types/executions').SessionTurn[] }>(
      `/session/${encodeURIComponent(sessionId)}/executions?projectID=${pid(projectID)}`,
    );
    return res?.turns ?? [];
  },

  async terminateExecution(
    projectID: string,
    executionId: string,
    _reason?: string,
  ): Promise<void> {
    await dataServiceClient.post<void>(
      `/execute_flow/${executionId}/cancel?projectID=${pid(projectID)}`,
    );
  },

  async restartExecution(
    projectID: string,
    executionId: string,
    agentId: string,
  ): Promise<{ execution_id: string }> {
    const res = await dataServiceClient.post<{ execution_id?: string; _id?: string }>(
      `/execute_flow/${executionId}/restart/${agentId}?projectID=${pid(projectID)}`,
    );
    return { execution_id: res?.execution_id ?? res?._id ?? executionId };
  },

  async submitRuntimeInput(
    projectID: string,
    executionId: string,
    agentId: string,
    inputs: UIFormElements[],
  ): Promise<void> {
    await dataServiceClient.put<void>(
      `/execute_flow/${executionId}/runtime_input?projectID=${pid(projectID)}`,
      { agent_id: agentId, is_submitted: true, values: toValuesMap(inputs) },
    );
  },

  // ── Interop: answer an A2A single-peer-agent task paused on input-required/
  // auth-required — SPEC-interop-agent-v2.md §8 test item #8. Only for the
  // 'a2a_pending' mechanism (InteropAsk); MCP's own bounded elicitation
  // answers directly against harness instead (agentsService.answerElicitation)
  // — there's no execute_flow-level pause to resume for that one.
  async submitInteropAnswer(
    projectID: string,
    executionId: string,
    action: 'accept' | 'decline' | 'cancel',
    content?: Record<string, any>,
  ): Promise<{ status: string }> {
    return dataServiceClient.put<{ status: string }>(
      `/execute_flow/${executionId}/interop_answer?projectID=${pid(projectID)}`,
      { action, content },
    );
  },

  async pauseExecution(projectID: string, executionId: string): Promise<void> {
    await dataServiceClient.post<void>(
      `/execute_flow/${executionId}/pause?projectID=${pid(projectID)}`,
    );
  },

  async resumeExecution(projectID: string, executionId: string): Promise<void> {
    await dataServiceClient.post<void>(
      `/execute_flow/${executionId}/resume?projectID=${pid(projectID)}`,
    );
  },

  /** Upload execution files through the data-service's supported two-step flow. */
  async uploadFiles(projectID: string, files: File[]): Promise<string[]> {
    if (files.length === 0) return [];
    return knowledgeBaseApi.uploadFile(projectID, { files, tags: ['execution-input'] });
  },

  async uploadFile(projectID: string, file: File): Promise<{ file_id: string }> {
    const [fileId] = await knowledgeBaseApi.uploadFile(projectID, {
      files: [file],
      tags: ['execution-input'],
    });
    if (!fileId) throw new Error('File upload completed without a file ID');
    return { file_id: fileId };
  },
};
